/** Shared trial end / countdown logic for tenant UI and platform admin. */

export interface TrialSourceOrg {
  status: string;
  trial_ends_at: string | null;
  created_at?: string | null;
}

export interface TrialSourceSubscription {
  status: string;
  trial_end: string | null;
}

export interface TrialSnapshot {
  endsAt: string | null;
  daysRemaining: number | null;
  isTrial: boolean;
  /** Which field drives the displayed end date */
  source: 'subscription' | 'organization' | null;
  /** True when org and subscription trial ends disagree */
  hasMismatch: boolean;
}

export function isOrgOnTrial(
  org: Pick<TrialSourceOrg, 'status'>,
  sub?: Pick<TrialSourceSubscription, 'status'> | null,
): boolean {
  return org.status === 'trial' || sub?.status === 'trialing';
}

/** Prefer subscription trial_end while trialing; otherwise org trial_ends_at. */
export function resolveTrialEndsAt(org: TrialSourceOrg, sub?: TrialSourceSubscription | null): string | null {
  if (!isOrgOnTrial(org, sub)) return null;

  const orgEnd = org.trial_ends_at;
  const subEnd = sub?.trial_end ?? null;

  if (sub?.status === 'trialing' && subEnd) return subEnd;
  return orgEnd;
}

export function trialDaysRemaining(endsAt: string | null, nowMs = Date.now()): number | null {
  if (!endsAt) return null;
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(endMs)) return null;
  return Math.max(0, Math.ceil((endMs - nowMs) / 86_400_000));
}

export function formatTrialEndDate(endsAt: string | null, locale = 'en-GB', timeZone = 'UTC'): string | null {
  if (!endsAt) return null;
  const d = new Date(endsAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  });
}

export function formatTrialEndDateTime(
  endsAt: string | null,
  locale = 'en-GB',
  timeZone = 'UTC',
): string | null {
  if (!endsAt) return null;
  const d = new Date(endsAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

export function buildTrialSnapshot(
  org: TrialSourceOrg,
  sub?: TrialSourceSubscription | null,
  nowMs = Date.now(),
): TrialSnapshot {
  const onTrial = isOrgOnTrial(org, sub);
  const orgEnd = org.trial_ends_at;
  const subEnd = sub?.trial_end ?? null;
  const endsAt = resolveTrialEndsAt(org, sub);
  const source: TrialSnapshot['source'] =
    !onTrial || !endsAt ? null : sub?.status === 'trialing' && subEnd ? 'subscription' : 'organization';

  const hasMismatch =
    onTrial &&
    Boolean(orgEnd && subEnd) &&
    Math.abs(new Date(orgEnd!).getTime() - new Date(subEnd!).getTime()) > 60_000;

  return {
    endsAt,
    daysRemaining: trialDaysRemaining(endsAt, nowMs),
    isTrial: onTrial,
    source,
    hasMismatch,
  };
}

/** Header chip: short label + rich tooltip */
export function buildTrialChipLabel(snapshot: TrialSnapshot): { label: string; title: string } {
  const endLabel = formatTrialEndDate(snapshot.endsAt);
  const days = snapshot.daysRemaining;

  if (days == null || !endLabel) {
    return { label: 'Trial', title: 'Free trial active — view plan & billing' };
  }

  if (days === 0) {
    return {
      label: 'Trial · ends today',
      title: `Trial ends today (${formatTrialEndDateTime(snapshot.endsAt) ?? endLabel})`,
    };
  }

  if (days <= 7) {
    return {
      label: `Trial · ${days} day${days === 1 ? '' : 's'} left`,
      title: `Trial ends ${endLabel} (${formatTrialEndDateTime(snapshot.endsAt) ?? endLabel})`,
    };
  }

  return {
    label: `Trial · ends ${endLabel}`,
    title: `${days} days remaining · ends ${formatTrialEndDateTime(snapshot.endsAt) ?? endLabel}`,
  };
}

/** Expected trial end from org signup + platform-configured trial length. */
export function expectedTrialEndFromSignup(
  createdAt: string | null | undefined,
  trialDays: number,
): string | null {
  if (!createdAt || !Number.isFinite(trialDays) || trialDays <= 0) return null;
  const start = new Date(createdAt);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + trialDays);
  return end.toISOString();
}
